import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { CheckStatusIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  readonly label?: ReactNode;
  readonly invalid?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, invalid, className, disabled, id, ...rest },
  ref,
) {
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none text-ink',
        'text-body leading-snug [@media(pointer:coarse)]:min-h-11',
        disabled && 'opacity-45 cursor-not-allowed',
        className,
      )}
    >
      <span className="relative inline-flex items-center justify-center">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'peer appearance-none size-4 [@media(pointer:coarse)]:size-5 rounded-sm bg-surface',
            'border border-ink-3/80',
            'transition-[background-color,border-color,box-shadow] duration-(--duration-tap) motion-reduce:transition-none',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text',
            'checked:bg-primary checked:border-primary',
            'disabled:cursor-not-allowed',
            invalid && 'border-danger focus-visible:ring-danger/25',
          )}
          {...rest}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center text-primary-ink opacity-0 peer-checked:opacity-100"
        >
          <CheckStatusIcon size={12} color="currentColor" />
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
});
